import React, { useCallback, useMemo, useState } from "react";

import Team from "@mapmycustomers/shared/types/Team";
import SelectableTeam from "@mapmycustomers/shared/types/ui/SelectableTeam";
import User from "@mapmycustomers/shared/types/User";

import { isOwner } from "@app/store/iam/util";

import Footer from "./components/Footer";
import TeamList from "./components/TeamList";
import UserList from "./components/UserList";
import styles from "./UsersSelect.module.scss";

interface UsersSelectProps {
  allowEmpty?: boolean;
  allUsersWhenEmpty?: boolean;
  cancelText?: string;
  confirmText?: string;
  isCurrentUserOwner: boolean;
  onApply: (userIds: User["id"][]) => void;
  onCancel: () => void;
  teams: Team[];
  userIds?: User["id"][];
  users: User[];
}

const UsersSelect: React.FC<UsersSelectProps> = ({
  allowEmpty,
  allUsersWhenEmpty = true,
  cancelText,
  confirmText,
  isCurrentUserOwner,
  onApply,
  onCancel,
  teams,
  userIds,
  users,
}) => {
  const allUserIds = useMemo(() => users.map(({ id }) => id), [users]);
  const ownerUserIds = useMemo(() => users.filter(isOwner).map(({ id }) => id), [users]);
  const noTeamUserIds = useMemo(
    () => users.filter((user) => !isOwner(user) && !user.teams.length).map(({ id }) => id),
    [users]
  );

  const [selectedUserIds, setSelectedUserIds] = useState<User["id"][]>(
    userIds?.length ? userIds : allUsersWhenEmpty ? allUserIds : []
  );
  const selectedUserIdsSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds]);

  const isTeamSelected = useCallback(
    (team: SelectableTeam) => {
      switch (team) {
        case "noTeam":
          return noTeamUserIds.every((id) => selectedUserIdsSet.has(id));
        case "owners":
          return ownerUserIds.every((id) => selectedUserIdsSet.has(id));
        default:
          return team.users.every(({ id }) => selectedUserIdsSet.has(id));
      }
    },
    [noTeamUserIds, ownerUserIds, selectedUserIdsSet]
  );

  const teamsByUserId = useMemo(() => new Map(users.map((user) => [user.id, user.teams])), [users]);

  const handleSelectAll = useCallback(() => setSelectedUserIds(allUserIds), [allUserIds]);
  const handleDeselectAll = useCallback(() => setSelectedUserIds([]), []);

  const handleTeamClick = useCallback(
    (team: SelectableTeam) => {
      const wasSelected = isTeamSelected(team);
      const userIds =
        team === "owners"
          ? ownerUserIds
          : team === "noTeam"
          ? noTeamUserIds
          : team.users.map(({ id }) => id);
      if (!wasSelected) {
        // when adding users, no special verification is needed, just add avoiding duplicates
        setSelectedUserIds((selectedUserIds) =>
          Array.from(new Set([...selectedUserIds, ...userIds]))
        );
      } else {
        const removableUserIds = new Set(
          // when removing owners or no-team users, no special verification needed, since these users
          // are not a part of any other teams except their own
          team === "owners" || team === "noTeam"
            ? userIds
            : userIds.filter((userId) => teamsByUserId.get(userId)!.length < 2)
        );
        setSelectedUserIds((selectedUserIds) =>
          selectedUserIds.filter((id) => !removableUserIds.has(id))
        );
      }
    },
    [isTeamSelected, ownerUserIds, noTeamUserIds, teamsByUserId]
  );

  const handleUserClick = useCallback((userId) => {
    setSelectedUserIds((selectedUserIds) => {
      const index = selectedUserIds.indexOf(userId);
      if (index >= 0) {
        return [...selectedUserIds.slice(0, index), ...selectedUserIds.slice(index + 1)];
      } else {
        return [...selectedUserIds, userId];
      }
    });
  }, []);

  const handleApply = useCallback(() => {
    onApply?.(selectedUserIds);
  }, [onApply, selectedUserIds]);

  return (
    <div className={styles.container}>
      <div className={styles.teamsAndUsersColumns}>
        <TeamList
          className={styles.teams}
          isCurrentUserOwner={isCurrentUserOwner}
          isTeamSelected={isTeamSelected}
          noTeamUserIds={noTeamUserIds}
          onTeamClick={handleTeamClick}
          teams={teams}
        />
        <UserList
          className={styles.users}
          onUserClick={handleUserClick}
          selectedUserIdsSet={selectedUserIdsSet}
          users={users}
        />
      </div>
      <Footer
        allowEmpty={allowEmpty}
        cancelText={cancelText}
        className={styles.footer}
        confirmText={confirmText}
        onApply={handleApply}
        onCancel={onCancel}
        onDeselectAll={handleDeselectAll}
        onSelectAll={handleSelectAll}
        selectedUserIds={selectedUserIds}
        users={users}
      />
    </div>
  );
};

export default UsersSelect;
